import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Star, Info, Volume2, VolumeX } from 'lucide-react';
import { storageKey } from '@/lib/storage-key.js';
import { OriginalIcon } from '../OriginalIcon.js';

interface Props {
  code: string;
  title: string;
  muted: boolean;
  onToggleSound: () => void;
  onFairness: () => void;
  onHowTo: () => void;
}

function favKey(code: string) {
  return storageKey(`originals.fav.${code}`);
}

export function GameInfoBar(p: Props) {
  const { t } = useTranslation('originals');
  const [fav, setFav] = useState(false);

  useEffect(() => {
    setFav(localStorage.getItem(favKey(p.code)) === '1');
  }, [p.code]);

  const toggleFav = useCallback(() => {
    setFav((cur) => {
      const next = !cur;
      localStorage.setItem(favKey(p.code), next ? '1' : '0');
      return next;
    });
  }, [p.code]);

  const btn =
    'grid place-items-center w-10 h-10 rounded-xl bg-[var(--bg-sunken)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-raised)] transition-colors';

  return (
    <div className="flex items-center gap-3.5 px-4 sm:px-5 py-4 rounded-2xl border border-transparent bg-[var(--bg-raised)]">
      <OriginalIcon code={p.code} size={48} />
      <div className="flex flex-col min-w-0">
        <span className="text-[17px] sm:text-[19px] font-black tracking-tight text-[var(--text-primary)] truncate">
          {p.title}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block rounded-md bg-[var(--primary)]/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--primary)]">
            Originals
          </span>
          <span className="text-[11px] text-[var(--text-muted)] truncate">{t('infobar.by')}</span>
        </span>
      </div>
      <div className="flex items-center gap-2 ml-auto">
        <button type="button" className={btn} aria-label={t('infobar.fairness')} onClick={p.onFairness}>
          <ShieldCheck size={18} />
        </button>
        <button type="button" className={btn} aria-label={t('infobar.howToPlay')} onClick={p.onHowTo}>
          <Info size={18} />
        </button>
        <button type="button" className={btn} aria-label={p.muted ? t('soundOn') : t('soundOff')} aria-pressed={p.muted} onClick={p.onToggleSound}>
          {p.muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
        <button type="button" className={btn} aria-label={t('infobar.favorite')} aria-pressed={fav} onClick={toggleFav}>
          <Star size={18} fill={fav ? 'currentColor' : 'none'} className={fav ? 'text-[var(--primary)]' : ''} />
        </button>
      </div>
    </div>
  );
}
